import format from 'date-fns/format';
import isValid from 'date-fns/isValid';
import toDate from 'date-fns/toDate';
import Cookies from 'js-cookie';
import Big from 'big.js';

export const hideNumber = (value: string) => '****';

export function formatDateWithLocale(date: number | Date, locale: string, type: 'DATE' | 'TIME' = 'TIME') {
  const baseFormat = locale === 'zh-tw' ? 'yyyy-MM-dd' : 'MM/dd/yyyy';
  return format(date, type === 'DATE' ? baseFormat : `${baseFormat} HH:mm:ss`);
}

export function toNonExponential(num: number): string {
  const m = num.toExponential().match(/\d(?:\.(\d*))?e([+-]\d+)/);
  return num.toFixed(Math.max(0, (m?.[1] || '').length - Number(m?.[2] || 0)));
}

type RadixPointType = 'REMOVE_ZERO' | 'SUPPLY_ZERO' | 'NORMAL';
type NumberConvertOptions = {
  precision?: number;
  separator?: boolean;
  radixPoint?: RadixPointType;
  round?: boolean;
};

export function toConvertNumber(str: string, options?: NumberConvertOptions): string {
  const { precision = 8, separator = true, round = false } = options || {};
  let radixPoint = options?.radixPoint || 'NORMAL';

  if (typeof str !== 'string' || !str.length || Number.isNaN(Number(str))) return '';
  let [int = '0', decimal = ''] = str.split('.');
  let sign = int.slice(0, 1); // for + -
  ['+', '-'].includes(sign) ? (int = int.slice(1)) : (sign = '');
  if (precision < 0) {
    const l = int.length - Math.abs(precision);
    return int === '0' ? int : `${sign === '-' ? sign : ''}${int.slice(0, l)}${'0'.repeat(Math.abs(precision))}`;
  }
  const decimalLen = decimal.length;
  int = Number(int) === 0 ? '0' : int;
  const intLen = int.length;

  if (round && precision < decimalLen) {
    decimal = decimal.slice(0, precision + 1);
    [int, decimal] = toRound({ int, decimal, precision, intLen, decimalLen: decimal.length });
    radixPoint = 'NORMAL';
  }

  const newInt = separator ? toFraction(int, undefined, intLen) : int;
  const newDecimal = toPrecision(
    decimal,
    precision,
    round && precision >= decimalLen ? 'SUPPLY_ZERO' : radixPoint,
    decimalLen
  );

  const r = newInt + newDecimal;
  return Number(r) === 0 || sign !== '-' ? r : sign + r;
}

export function toFraction(str = '', place = 3, originStringLen?: number): string {
  const reg = new RegExp(`(\\d)(?=(?:\\d{${place}})+$)`, 'g');
  return (originStringLen ?? str.length) > place ? str.replace(reg, '$1,') : str;
}

function toPrecision(str: string, precision: number, type: RadixPointType, originStringLen?: number) {
  if (typeof str !== 'string' || precision <= 0) return '';
  let len = originStringLen ?? str.length;

  switch (type) {
    case 'NORMAL':
      return len === 0 ? '' : '.' + str.slice(0, precision);
    case 'SUPPLY_ZERO':
      return '.' + (len < precision ? str.padEnd(precision, '0') : str.slice(0, precision));
    default: {
      let i = 1;
      len = Math.min(len, precision);
      str = str.slice(0, len);
      while (i && i <= Math.ceil(len / 16)) {
        const temp = Number(`0.${str.slice(-16)}`);
        if (temp === 0) {
          str = str.slice(0, -16);
          i++;
        } else {
          str = str.slice(0, -16) + toNonExponential(temp).slice(2);
          i = 0;
        }
      }
      return str ? `.${str}` : '';
    }
  }
}

type ToRoundProps = {
  int: string;
  decimal: string;
  intLen?: number;
  decimalLen?: number;
  precision: number;
};

function toRound({ int, decimal, precision, intLen = 0, decimalLen = 0 }: ToRoundProps) {
  const safeDecimalLen = 8,
    safeIntLen = 15;
  intLen = intLen || int.length;
  decimalLen = decimalLen || decimal.length;
  let i = 1,
    dCarryFlag = false,
    returnDecimal = '';
  while (i && i <= Math.ceil(decimalLen / safeDecimalLen)) {
    const newDecimal = decimal.slice(-i * safeDecimalLen, (1 - i) * safeDecimalLen || precision + 1);
    const newDecimalLen = newDecimal.length;
    let status: 'INCREASE' | 'DAP' = 'INCREASE';

    if (i === 1) {
      const temp = Number(`0.${newDecimal}`).toFixed(Math.min(precision, safeDecimalLen - 1));
      if (temp.startsWith('1')) {
        returnDecimal = '0'.repeat(newDecimalLen - 1);
      } else {
        returnDecimal = decimal.slice(0, -safeDecimalLen) + temp.slice(2);
        status = 'DAP';
      }
    } else {
      const temp = (Number(newDecimal) + 1).toString();
      if (temp.length > newDecimalLen) {
        returnDecimal = '0'.repeat(newDecimalLen) + returnDecimal;
      } else {
        returnDecimal = decimal.slice(0, -i * safeDecimalLen) + temp + returnDecimal;
        status = 'DAP';
      }
    }

    if (status === 'INCREASE') {
      i++;
      dCarryFlag = true;
    } else {
      i = 0;
      dCarryFlag = false;
    }
  }
  i = 1; // reset

  let iCarryFlag = false,
    returnInt = '';
  if (dCarryFlag) {
    while (i && i <= Math.ceil(intLen / safeIntLen)) {
      const newInt = int.slice(-i * safeIntLen, (1 - i) * safeIntLen || intLen);
      const newIntLen = newInt.length;
      const temp = (Number(newInt) + 1).toString();
      if (temp.length > newIntLen) {
        i++;
        iCarryFlag = true;
        returnInt = '0'.repeat(newIntLen) + returnInt;
      } else {
        returnInt = int.slice(0, -i * safeIntLen) + temp + returnInt;
        i = 0;
        iCarryFlag = false;
      }
    }
  }

  return [iCarryFlag ? `1${'0'.repeat(int.length)}` : returnInt || int, returnDecimal];
}

export const clipImg = (
  value: string,
  width: number,
  height: number,
  fit: string,
  type = 'blockfin',
  quality = 100
) => {
  if (!value) {
    return;
  }
  if (type === 'blockfin') {
    return `https://blockfin.com/cdn-cgi/image/width=${width},height=${height},format=webp,quality=${quality},fit=${fit}/${value}`;
  } else {
    return `https://tokeninsight.com/cdn-cgi/image/width=${width},height=${height},format=webp,quality=${quality},fit=${fit}/${value}`;
  }
};

interface FormatPart {
  isToken: boolean;
  value: string;
}

function cleanEscapedString(input: string): string {
  const escapedStringRegExp = /^'([^]*?)'?$/;
  const doubleQuoteRegExp = /''/g;

  const matched = input.match(escapedStringRegExp);

  if (!matched) {
    return input;
  }

  return matched[1].replace(doubleQuoteRegExp, "'");
}

export function formatDate<DateType extends Date>(
  date: DateType | number,
  formatStr: string,
  options?: {
    locale?: Locale;
    weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
    firstWeekContainsDate?: number;
    useAdditionalWeekYearTokens?: boolean;
    useAdditionalDayOfYearTokens?: boolean;
  },
  lang?: string
): string {
  const list = ['Y', 'y', 'm', 'M', 'd', 'D', 'H', 'h', 'S', 's', '/', '-', ':', ' '];

  const formattingTokensRegExp = /[yYQqMLwIdDecihHKkms]o|(\w)\1*|''|'(''|[^'])+('|$)|./g;
  const unescapedLatinCharacterRegExp = /[a-zA-Z]/;

  const originalDate = toDate(date);

  if (!isValid(originalDate)) {
    throw new RangeError('Invalid time value');
  }

  const parts: FormatPart[] = formatStr.match(formattingTokensRegExp)!.map((substring) => {
    if (substring === "''") {
      return { isToken: false, value: "'" };
    }

    const firstCharacter = substring[0];
    if (firstCharacter === "'") {
      return { isToken: false, value: cleanEscapedString(substring) };
    }

    if (list.includes(firstCharacter)) {
      return { isToken: true, value: substring };
    }

    if (firstCharacter.match(unescapedLatinCharacterRegExp)) {
      throw new RangeError('Format string contains an unescaped latin alphabet character `' + firstCharacter + '`');
    }

    return { isToken: false, value: substring };
  });

  const flag = parts.some((item) => !item.isToken);
  const formatValues = parts.map((item) => item.value.toLowerCase());
  const hasY = formatValues.includes('yyyy');
  const hasM = formatValues.includes('mm');
  const hasD = formatValues.includes('dd');
  const has_ = formatValues.includes('-');

  if (!flag && hasY && hasM && hasD && !has_) {
    const locale = lang || Cookies.get('lang') || 'en';
    const str = formatStr.split(' ');
    const dateFormat = str[1]
      ? (locale === 'zh-tw' ? 'yyyy/MM/dd' : 'MM/dd/yyyy') + ' ' + str[1]
      : locale === 'zh-tw'
        ? 'yyyy/MM/dd'
        : 'MM/dd/yyyy';
    return format(originalDate, dateFormat, options);
  }

  return format(originalDate, formatStr, options);
}

export function toNumberAliased(str: string) {
  if (Number(str) < 1e6) return toConvertNumber(str, { precision: 2 });
  if (Number(str) >= 1e9) return `${toConvertNumber(new Big(str).div(1e9).toString(), { precision: 2 })}B`;
  return `${toConvertNumber(new Big(str).div(1e6).toString(), { precision: 2 })}M`;
}
